import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { labels } from "../i18n/translations";
import { fetchPerfume } from "../lib/api";
import WhatsAppButton from "../components/WhatsAppButton";
import ImagePlaceholder from "../components/ImagePlaceholder";
import ImageLightbox from "../components/ImageLightbox";

const Attr = ({ label, value, testId }) =>
  value ? (
    <div data-testid={testId} className="py-5 border-b border-black/10">
      <div className="uppercase tracking-[0.22em] text-[10px] text-black/45 mb-2">
        {label}
      </div>
      <div className="font-heading text-lg md:text-xl tracking-tight">{value}</div>
    </div>
  ) : null;

export const ProductDetail = () => {
  const { id } = useParams();
  const [perfume, setPerfume] = useState(null);
  const [error, setError] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setPerfume(null);
    setError(false);
    fetchPerfume(id)
      .then((p) => {
        if (!mounted) return;
        setPerfume(p);
        setActiveImage(p.main_image || null);
      })
      .catch(() => mounted && setError(true));
    return () => {
      mounted = false;
    };
  }, [id]);

  if (error) {
    return (
      <main
        data-testid="page-product-not-found"
        className="px-6 md:px-12 lg:px-20 py-32 text-center"
      >
        <div className="font-heading text-4xl md:text-5xl font-light mb-8">
          {labels.product.not_found}
        </div>
        <Link
          to="/catalogo"
          className="uppercase tracking-[0.22em] text-[11px] border-b border-black pb-1 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
        >
          {labels.product.back}
        </Link>
      </main>
    );
  }

  if (!perfume) {
    return (
      <main className="px-6 md:px-12 lg:px-20 py-32 text-center uppercase tracking-[0.32em] text-[11px] text-black/40">
        {labels.catalog.loading}
      </main>
    );
  }

  const displayName = perfume.name || `Profumo ${perfume.slot_number}`;
  const message = labels.product.whatsapp_msg(displayName);
  const additional = (perfume.additional_images || []).filter(Boolean).slice(0, 3);
  const allImages = [perfume.main_image, ...additional].filter(Boolean);

  return (
    <main data-testid={`page-product-${perfume.id}`} className="pt-8 md:pt-12">
      <div className="px-6 md:px-12 lg:px-20 mb-6 md:mb-10">
        <Link
          to="/catalogo"
          data-testid="back-to-catalog"
          className="uppercase tracking-[0.22em] text-[11px] text-black/60 hover:text-[#C5A059] transition-colors"
        >
          {labels.product.back}
        </Link>
      </div>

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 space-y-4">
            <button
              type="button"
              onClick={() => activeImage && setLightboxOpen(true)}
              data-testid="product-image"
              aria-label={activeImage ? "Apri immagine a schermo intero" : ""}
              className={`relative aspect-[4/5] md:aspect-auto md:h-[500px] lg:h-[650px] w-full overflow-hidden bg-[#F4F4F4] flex items-center justify-center ${
                activeImage ? "cursor-zoom-in group" : ""
              }`}
            >
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={`${perfume.brand || ""} ${displayName}`.trim()}
                  className="max-w-[85%] max-h-[85%] w-auto h-auto object-contain object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              ) : (
                <ImagePlaceholder />
              )}
            </button>

            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImage(img)}
                    data-testid={`thumb-${i}`}
                    className={`relative aspect-square overflow-hidden bg-[#F4F4F4] border flex items-center justify-center ${
                      activeImage === img ? "border-[#C5A059]" : "border-transparent"
                    } transition-colors`}
                  >
                    <img
                      src={img}
                      alt=""
                      className="max-w-[85%] max-h-[85%] w-auto h-auto object-contain object-center"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div
            data-testid="product-brand"
            className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-4 min-h-[16px]"
          >
            {perfume.brand || labels.product.no_data}
          </div>
          <h1
            data-testid="product-name"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[1] text-black"
          >
            {displayName}
          </h1>

          {perfume.price && (
            <div
              data-testid="product-price"
              className="mt-6 font-heading text-2xl md:text-3xl tracking-tight text-black"
            >
              {perfume.price}
            </div>
          )}

          {perfume.short_description && (
            <p
              data-testid="product-short-description"
              className="mt-8 text-base md:text-lg text-black/70 font-light leading-relaxed italic"
            >
              {perfume.short_description}
            </p>
          )}

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            <Attr label={labels.product.format} value={perfume.size_ml} testId="attr-format" />
            <Attr label={labels.product.status} value={perfume.condition} testId="attr-status" />
            <Attr label={labels.product.availability} value={perfume.availability} testId="attr-availability" />
            <Attr label={labels.product.gender} value={perfume.gender} testId="attr-gender" />
            <Attr label={labels.product.year} value={perfume.year} testId="attr-year" />
            <Attr label={labels.product.concentration} value={perfume.concentration} testId="attr-concentration" />
            <Attr label={labels.product.season} value={perfume.seasonality} testId="attr-season" />
            <Attr label={labels.product.sillage} value={perfume.sillage} testId="attr-sillage" />
            <Attr label={labels.product.longevity} value={perfume.longevity} testId="attr-longevity" />
          </div>

          {perfume.olfactory_notes && (
            <div data-testid="product-notes" className="mt-10">
              <div className="uppercase tracking-[0.22em] text-[10px] text-black/45 mb-3">
                {labels.product.notes}
              </div>
              <p className="font-light leading-relaxed text-black/75">
                {perfume.olfactory_notes}
              </p>
            </div>
          )}

          {perfume.full_description && (
            <div data-testid="product-full-description" className="mt-10">
              <div className="uppercase tracking-[0.22em] text-[10px] text-black/45 mb-3">
                {labels.product.full_description}
              </div>
              <p className="font-light leading-relaxed text-black/75 whitespace-pre-line">
                {perfume.full_description}
              </p>
            </div>
          )}

          <div className="mt-12">
            <WhatsAppButton
              testId="product-whatsapp-cta"
              message={message}
              size="full"
              variant="gold"
            >
              {labels.product.whatsapp_cta}
            </WhatsAppButton>
            <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-black/40 text-center">
              Risposta entro 24 ore · Spedizione tracciata
            </p>
          </div>
        </div>
      </section>

      <ImageLightbox
        src={activeImage}
        alt={`${perfume.brand || ""} ${displayName}`.trim()}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </main>
  );
};

export default ProductDetail;
